/**
 * Settings → Inspection module.
 * Overview (linked buildings + schedule), default checklist, asset field defs.
 * Existing SS checklist templates and the building Chiller tab are unchanged.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import InspectionQuestionEditor from '../../components/InspectionQuestionEditor';
import { resolveSettingsNav } from '../../navigation/settingsNavigation';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'checklist', label: 'Checklist' },
  { id: 'devices', label: 'Inspection devices' },
];

const FREQ = [
  { id: 'QUARTERLY', label: 'Quarterly' },
  { id: 'HALF_YEARLY', label: 'Half-yearly' },
  { id: 'YEARLY', label: 'Yearly' },
];

function tabButton(active, onClick, label) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${
        active ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}

function buildingModulePath(moduleCode, buildingId) {
  if (String(moduleCode).toUpperCase() === 'CHILLER') return `/buildings/${buildingId}/chiller`;
  return null;
}

export default function InspectionModuleSettings() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const moduleCode = params.get('module') || '';
  const buildingId = params.get('building') || '';

  function setNav(next) {
    const n = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (!v) n.delete(k);
      else n.set(k, v);
    });
    setParams(n);
  }

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveSettingsNav}
        title="Inspection module"
        infoTitle="Inspection module"
        infoChildren={
          <p className="text-sm">
            Cassette settings for plant inspections. Questions and asset fields are the master copy.
            Each linked building keeps its own checklist after the first copy. BWOF specified systems
            are unchanged.
          </p>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) =>
          tabButton(tab === t.id, () => setNav({ tab: t.id, module: '', building: '' }), t.label)
        )}
      </div>
      {tab === 'overview' && !moduleCode && <OverviewList onOpen={(code) => setNav({ tab: 'overview', module: code })} />}
      {tab === 'overview' && moduleCode && !buildingId && (
        <BuildingList
          moduleCode={moduleCode}
          onBack={() => setNav({ tab: 'overview', module: '' })}
          onOpen={(id) => {
            const path = buildingModulePath(moduleCode, id);
            if (path) navigate(path);
            else setNav({ tab: 'overview', module: moduleCode, building: id });
          }}
        />
      )}
      {tab === 'overview' && moduleCode && buildingId && (
        <BuildingSettings
          moduleCode={moduleCode}
          buildingId={buildingId}
          onBack={() => setNav({ tab: 'overview', module: moduleCode, building: '' })}
        />
      )}
      {tab === 'checklist' && !moduleCode && (
        <DeviceTypeList onOpen={(code) => setNav({ tab: 'checklist', module: code })} />
      )}
      {tab === 'checklist' && moduleCode && (
        <DefaultChecklist
          moduleCode={moduleCode}
          onBack={() => setNav({ tab: 'checklist', module: '' })}
        />
      )}
      {tab === 'devices' && !moduleCode && (
        <DeviceTypeList allowAdd onOpen={(code) => setNav({ tab: 'devices', module: code })} />
      )}
      {tab === 'devices' && moduleCode && (
        <AssetFields moduleCode={moduleCode} onBack={() => setNav({ tab: 'devices', module: '' })} />
      )}
    </div>
  );
}

function OverviewList({ onOpen }) {
  const queryClient = useQueryClient();
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['inspection-modules', 'overview'],
    queryFn: async () => (await api.get('/inspection-modules/overview')).data?.data || [],
  });
  const toggle = useMutation({
    mutationFn: async ({ code, enabled }) =>
      (await api.put(`/inspection-modules/${code}/enabled`, { enabled })).data?.data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['chiller', 'status'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not update module'),
  });

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (isError) return <p className="text-sm text-red-600">{error?.response?.data?.error || 'Failed to load'}</p>;

  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3">Inspection</th>
            <th className="px-4 py-3">Linked buildings</th>
            <th className="px-4 py-3">Module</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.map((row) => (
            <tr key={row.module_code} className="hover:bg-blue-50/40">
              <td className="px-4 py-3">
                <button type="button" className="text-left" onClick={() => onOpen(row.module_code)}>
                  <p className="font-semibold text-primary-700">{row.label}</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-xl">{row.description}</p>
                </button>
              </td>
              <td className="px-4 py-3 tabular-nums font-semibold">{row.linked_buildings}</td>
              <td className="px-4 py-3">
                <label className="inline-flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={!!row.enabled}
                    onChange={(e) => toggle.mutate({ code: row.module_code, enabled: e.target.checked })}
                  />
                  {row.enabled ? 'On' : 'Off'}
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BuildingList({ moduleCode, onBack, onOpen }) {
  const title = moduleCode === 'CHILLER' ? 'Chiller inspection list' : `${moduleCode} inspection list`;
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['inspection-modules', moduleCode, 'buildings'],
    queryFn: async () => (await api.get(`/inspection-modules/${moduleCode}/buildings`)).data?.data || [],
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button type="button" className="text-sm text-primary-700" onClick={onBack}>
          ← Overview
        </button>
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <p className="text-xs text-gray-500">
        Open a row to go to that building’s {moduleCode === 'CHILLER' ? 'Chiller' : moduleCode} page.
        Next month is calculated from the method and start month.
      </p>
      <div className="rounded-xl border bg-white overflow-hidden">
        {isLoading && <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>}
        {isError && <p className="px-4 py-6 text-sm text-red-600">{error?.response?.data?.error || 'Failed'}</p>}
        {!isLoading && !isError && (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Building</th>
                <th className="px-4 py-3">Start month</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Next inspection</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.map((b) => (
                <tr key={b.id} className="hover:bg-blue-50/40 cursor-pointer" onClick={() => onOpen(String(b.id))}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{b.name}</p>
                    <p className="text-xs text-gray-500">{b.address || '—'}</p>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{b.cycle_start_month}</td>
                  <td className="px-4 py-3">{b.frequency}</td>
                  <td className="px-4 py-3 tabular-nums">{b.next_inspection_month || '—'}</td>
                </tr>
              ))}
              {!data.length && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No linked buildings. Tick “This building has chiller / plant” on Edit Building.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BuildingSettings({ moduleCode, buildingId, onBack }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
    queryFn: async () =>
      (await api.get(`/inspection-modules/${moduleCode}/buildings/${buildingId}`)).data?.data,
  });
  const [form, setForm] = useState(null);
  const view = form || data;

  const saveSchedule = useMutation({
    mutationFn: async (payload) =>
      (await api.put(`/inspection-modules/${moduleCode}/buildings/${buildingId}/settings`, payload)).data
        ?.data,
    onSuccess: async () => {
      toast.success('Schedule saved');
      setForm(null);
      await queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode] });
      await queryClient.refetchQueries({ queryKey: ['inspection-modules', moduleCode], type: 'active' });
      await queryClient.invalidateQueries({ queryKey: ['building', buildingId, moduleCode] });
      await queryClient.refetchQueries({ queryKey: ['building', buildingId, moduleCode], type: 'active' });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const saveSet = useMutation({
    mutationFn: async ({ code, body }) =>
      (
        await api.put(
          `/inspection-modules/${moduleCode}/buildings/${buildingId}/sets/${code}`,
          body
        )
      ).data?.data,
    onSuccess: async () => {
      toast.success('Checklist saved for this building');
      await queryClient.invalidateQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
      });
      await queryClient.refetchQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
        type: 'active',
      });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const removeSet = useMutation({
    mutationFn: async (code) =>
      (await api.delete(`/inspection-modules/${moduleCode}/buildings/${buildingId}/sets/${code}`)).data,
    onSuccess: async () => {
      toast.success('Pack hidden');
      await queryClient.invalidateQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
      });
      await queryClient.refetchQueries({
        queryKey: ['inspection-modules', moduleCode, 'building', buildingId],
        type: 'active',
      });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  const restoreSet = useMutation({
    mutationFn: async (code) =>
      (await api.post(`/inspection-modules/${moduleCode}/buildings/${buildingId}/sets/${code}/restore`)).data,
    onSuccess: () => {
      toast.success('Pack shown');
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode, 'building', buildingId] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not show pack'),
  });

  if (isLoading || !view) return <p className="text-sm text-gray-500">Loading…</p>;
  if (isError) return <p className="text-sm text-red-600">{error?.response?.data?.error || 'Failed'}</p>;

  const plan = view.package_plan || {};
  const sets = view.checklist_sets || data?.checklist_sets || [];

  function togglePack(slot, code) {
    const base = form || data;
    const nextPlan = { ...(base.package_plan || {}) };
    const cur = { packs: [], calibration: false, ...(nextPlan[slot] || {}) };
    const packs = new Set((cur.packs || []).map((p) => String(p).toUpperCase()));
    if (packs.has(code)) packs.delete(code);
    else packs.add(code);
    nextPlan[slot] = { ...cur, packs: Array.from(packs) };
    setForm({ ...base, package_plan: nextPlan });
  }

  const slots = view.frequency === 'YEARLY' ? ['Y1'] : view.frequency === 'HALF_YEARLY' ? ['H1', 'H2'] : ['Q1', 'Q2', 'Q3', 'Q4'];

  return (
    <div className="space-y-4">
      <button type="button" className="text-sm text-primary-700" onClick={onBack}>
        ← {moduleCode === 'CHILLER' ? 'Chiller inspection list' : 'List'}
      </button>
      <h2 className="text-lg font-semibold">{data?.building_id ? `Building ${buildingId}` : buildingId}</h2>
      <p className="text-xs text-gray-500">
        Next inspection month: <span className="font-semibold tabular-nums">{view.next_inspection_month || '—'}</span>
        . Checklist below is this building’s copy. Changing the master checklist does not overwrite it.
      </p>
      <div className="rounded-xl border bg-white p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <label className="text-sm">
            Method
            <select
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={view.frequency}
              onChange={(e) => setForm({ ...(form || data), frequency: e.target.value })}
            >
              {FREQ.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            Start month
            <select
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={view.cycle_start_month}
              onChange={(e) =>
                setForm({ ...(form || data), cycle_start_month: Number(e.target.value) })
              }
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>
        {slots.map((slot) => {
          const packs = new Set((plan[slot]?.packs || []).map((p) => String(p).toUpperCase()));
          return (
            <div key={slot} className="flex flex-wrap items-center gap-3 text-sm">
              <span className="w-8 font-semibold">{slot}</span>
              {sets.map((s) => (
                <label key={s.code} className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={packs.has(s.code)} onChange={() => togglePack(slot, s.code)} />
                  {s.code}
                </label>
              ))}
              {!sets.length && <span className="text-gray-500">No checklist copy yet. Add a default checklist first.</span>}
            </div>
          );
        })}
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-50"
          disabled={saveSchedule.isPending}
          onClick={() =>
            saveSchedule.mutate({
              frequency: view.frequency,
              cycle_start_month: view.cycle_start_month,
              package_plan: view.package_plan,
              notes: view.notes || null,
            })
          }
        >
          Save schedule
        </button>
      </div>
      <CompanyPacks
        sets={sets}
        hiddenSets={view.hidden_checklist_sets || []}
        saving={saveSet.isPending}
        onSave={(code, body) => saveSet.mutate({ code, body })}
        onDelete={(code) => removeSet.mutate(code)}
        onRestore={(code) => restoreSet.mutate(code)}
      />
    </div>
  );
}

function DeviceTypeList({ allowAdd = false, onOpen }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['inspection-modules', 'device-types'],
    queryFn: async () => (await api.get('/inspection-modules/device-types')).data?.data || [],
  });
  const add = useMutation({
    mutationFn: async (body) => (await api.post('/inspection-modules/device-types', body)).data?.data,
    onSuccess: () => {
      toast.success('Device added');
      setAdding(false);
      setName('');
      setCode('');
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', 'device-types'] });
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', 'overview'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not add device'),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-600">Choose an inspection device. Chiller is the first one.</p>
        {allowAdd && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white font-semibold"
            onClick={() => setAdding((v) => !v)}
          >
            Add device
          </button>
        )}
      </div>
      {adding && (
        <div className="rounded-xl border bg-white p-4 grid gap-2 max-w-md">
          <label className="text-sm">
            Name
            <input
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={name}
              placeholder="Fire alarm"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Code
            <input
              className="mt-1 w-full border rounded px-2 py-1.5"
              value={code}
              placeholder="FIRE_ALARM"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </label>
          <p className="text-xs text-gray-500">Code is the device type key. Name is what people see.</p>
          <button
            type="button"
            className="justify-self-start text-sm rounded bg-primary-600 text-white px-3 py-1.5 disabled:opacity-50"
            disabled={add.isPending || !name.trim() || !code.trim()}
            onClick={() => add.mutate({ label: name.trim(), code: code.trim() })}
          >
            Save device
          </button>
        </div>
      )}
      <div className="rounded-xl border bg-white overflow-hidden">
        {isLoading && <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>}
        {isError && <p className="px-4 py-6 text-sm text-red-600">{error?.response?.data?.error || 'Failed to load'}</p>}
        {!isLoading && !isError && (
          <ul className="divide-y">
            {data.map((row) => (
              <li key={row.module_code}>
                <button type="button" className="w-full text-left px-4 py-3 hover:bg-blue-50/40" onClick={() => onOpen(row.module_code)}>
                  <p className="font-semibold text-primary-700">{row.label}</p>
                  <p className="text-xs text-gray-500">{row.module_code}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CompanyPacks({ sets, hiddenSets = [], onSave, onDelete, onRestore, saving = false }) {
  const [draftCode, setDraftCode] = useState('A');
  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-sm">Checklist for this building</h3>
      <div className="flex gap-2 items-end">
        <label className="text-sm">
          New pack code
          <input className="mt-1 block border rounded px-2 py-1.5 w-24" value={draftCode} onChange={(e) => setDraftCode(e.target.value.toUpperCase())} />
        </label>
        <button
          type="button"
          className="px-3 py-1.5 text-sm border rounded"
          onClick={() => onSave(draftCode, { code: draftCode, name: `Pack ${draftCode}`, items: [] })}
        >
          Add pack
        </button>
      </div>
      {sets.map((set) => (
        <InspectionQuestionEditor
          key={set.code}
          pack={set}
          saving={saving}
          onSave={(code, body) => onSave(code, body)}
          onDelete={onDelete}
        />
      ))}
      {hiddenSets.map((set) => (
        <div key={`hidden-${set.code}`} className="flex items-center justify-between rounded-xl border border-dashed bg-slate-50 px-4 py-3 text-sm">
          <span>
            <span className="font-semibold">{set.code}</span>
            <span className="text-gray-500"> · {set.name || 'Pack'} · hidden</span>
          </span>
          <button type="button" className="px-3 py-1.5 rounded bg-primary-600 text-white text-sm font-semibold" onClick={() => onRestore(set.code)}>
            Show
          </button>
        </div>
      ))}
    </div>
  );
}

function DefaultChecklist({ moduleCode, onBack }) {
  const queryClient = useQueryClient();
  const { data: types = [] } = useQuery({
    queryKey: ['inspection-modules', 'device-types'],
    queryFn: async () => (await api.get('/inspection-modules/device-types')).data?.data || [],
  });
  const deviceLabel = types.find((row) => row.module_code === moduleCode)?.label || moduleCode;
  const { data = [], isLoading } = useQuery({
    queryKey: ['inspection-modules', moduleCode, 'defaults'],
    queryFn: async () => (await api.get(`/inspection-modules/${moduleCode}/defaults`)).data?.data || [],
  });
  const { data: hidden = [] } = useQuery({
    queryKey: ['inspection-modules', moduleCode, 'defaults', 'hidden'],
    queryFn: async () =>
      (await api.get(`/inspection-modules/${moduleCode}/defaults`, { params: { status: 'hidden' } })).data?.data ||
      [],
  });
  const [code, setCode] = useState('D');

  const save = useMutation({
    mutationFn: async (body) =>
      (await api.put(`/inspection-modules/${moduleCode}/defaults/${body.code}`, body)).data?.data,
    onSuccess: () => {
      toast.success('Default pack saved');
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode, 'defaults'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });
  const remove = useMutation({
    mutationFn: async (pack) => (await api.delete(`/inspection-modules/${moduleCode}/defaults/${pack}`)).data,
    onSuccess: () => {
      toast.success('Pack hidden');
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode, 'defaults'] });
    },
  });
  const restore = useMutation({
    mutationFn: async (pack) =>
      (await api.post(`/inspection-modules/${moduleCode}/defaults/${pack}/restore`)).data,
    onSuccess: () => {
      toast.success('Pack shown');
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode, 'defaults'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not show pack'),
  });

  return (
    <div className="space-y-4">
      <div>
        <button type="button" className="text-sm text-primary-700" onClick={onBack}>
          ← Checklist
        </button>
        <h2 className="text-lg font-semibold mt-2">{deviceLabel} default checklist</h2>
        <p className="text-xs text-gray-500 mt-1">
          The chiller default is a sealed fridge or freezer (pack A). Pack P is the piped plant, and packs B and C are extra annual work. A building copy can add or remove questions. Changing this master list does not overwrite a copy already on a building.
        </p>
      </div>
      <div className="flex gap-2 items-end">
        <label className="text-sm">
          New pack code
          <input
            className="mt-1 block border rounded px-2 py-1.5 w-24"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </label>
        <button
          type="button"
          className="px-3 py-1.5 text-sm border rounded"
          onClick={() =>
            save.mutate({
              code,
              name: `Pack ${code}`,
              items: [],
              sort_order: (data || []).reduce((max, row) => Math.max(max, Number(row.sort_order) || 0), 0) + 1,
            })
          }
        >
          Add pack
        </button>
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {(data || []).map((set) => (
        <InspectionQuestionEditor
          key={`${set.code}-${set.updated_at || ''}`}
          pack={set}
          saving={save.isPending && save.variables?.code === set.code}
          onSave={(pack, body) => save.mutate({ ...body, code: pack })}
          onDelete={(pack) => remove.mutate(pack)}
        />
      ))}
      {(hidden || []).map((set) => (
        <div key={`hidden-${set.code}`} className="flex items-center justify-between rounded-xl border border-dashed bg-slate-50 px-4 py-3 text-sm">
          <span>
            <span className="font-semibold">{set.code}</span>
            <span className="text-gray-500"> · {set.name || 'Pack'} · hidden</span>
          </span>
          <button
            type="button"
            className="px-3 py-1.5 rounded bg-primary-600 text-white text-sm font-semibold"
            onClick={() => restore.mutate(set.code)}
          >
            Show
          </button>
        </div>
      ))}
      {!isLoading && !data.length && !(hidden || []).length && (
        <p className="text-sm text-gray-500">No default packs yet.</p>
      )}
    </div>
  );
}

function AssetFields({ moduleCode, onBack }) {
  const queryClient = useQueryClient();
  const { data: types = [] } = useQuery({
    queryKey: ['inspection-modules', 'device-types'],
    queryFn: async () => (await api.get('/inspection-modules/device-types')).data?.data || [],
  });
  const deviceLabel = types.find((row) => row.module_code === moduleCode)?.label || moduleCode;
  const { data = [], isLoading } = useQuery({
    queryKey: ['inspection-modules', moduleCode, 'fields'],
    queryFn: async () => (await api.get(`/inspection-modules/${moduleCode}/fields`)).data?.data || [],
  });
  const [form, setForm] = useState({ field_key: '', label: '', data_type: 'text' });
  const [paste, setPaste] = useState('');
  const [drafts, setDrafts] = useState([]);

  const save = useMutation({
    mutationFn: async (body) =>
      (await api.put(`/inspection-modules/${moduleCode}/fields/${body.field_key}`, body)).data?.data,
    onSuccess: () => {
      setForm({ field_key: '', label: '', data_type: 'text' });
      queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode, 'fields'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });
  const remove = useMutation({
    mutationFn: async (key) => (await api.delete(`/inspection-modules/${moduleCode}/fields/${key}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inspection-modules', moduleCode, 'fields'] }),
  });
  const extract = useMutation({
    mutationFn: async ({ text, file }) => {
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        return (await api.post(`/inspection-modules/${moduleCode}/fields/extract`, fd)).data?.data;
      }
      return (await api.post(`/inspection-modules/${moduleCode}/fields/extract`, { text })).data?.data;
    },
    onSuccess: (res) => setDrafts(res?.fields || []),
    onError: (e) => toast.error(e.response?.data?.error || 'Extract failed'),
  });

  return (
    <div className="space-y-4">
      <div>
        <button type="button" className="text-sm text-primary-700" onClick={onBack}>
          ← Devices
        </button>
        <h2 className="text-lg font-semibold mt-2">{deviceLabel} asset fields</h2>
        <p className="text-xs text-gray-500 mt-1">
          Fields collected for each chiller. Nameplate or document upload only creates a draft — nothing is saved until you confirm.
        </p>
      </div>
      <div className="rounded-xl border bg-white p-4 grid gap-2 max-w-xl">
        <label className="text-sm">
          Key
          <input className="mt-1 w-full border rounded px-2 py-1.5" value={form.field_key} onChange={(e) => setForm({ ...form, field_key: e.target.value.toLowerCase() })} placeholder="refrigerant" />
        </label>
        <label className="text-sm">
          Label
          <input className="mt-1 w-full border rounded px-2 py-1.5" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Refrigerant" />
        </label>
        <label className="text-sm">
          Type
          <select className="mt-1 w-full border rounded px-2 py-1.5" value={form.data_type} onChange={(e) => setForm({ ...form, data_type: e.target.value })}>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
          </select>
        </label>
        <button type="button" className="justify-self-start text-sm rounded bg-primary-600 text-white px-3 py-1.5" onClick={() => save.mutate(form)}>
          Add field
        </button>
      </div>
      <div className="rounded-xl border bg-white p-4 space-y-2">
        <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={3} placeholder="One field label per line" value={paste} onChange={(e) => setPaste(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          <button type="button" className="text-sm border rounded px-3 py-1.5" onClick={() => extract.mutate({ text: paste })}>
            Draft from text
          </button>
          <label className="text-sm border rounded px-3 py-1.5 cursor-pointer">
            Upload txt / csv / pdf
            <input
              type="file"
              accept=".txt,.csv,.pdf,text/plain,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) extract.mutate({ file });
                e.target.value = '';
              }}
            />
          </label>
        </div>
        {drafts.map((d) => (
          <div key={d.field_key} className="flex justify-between text-sm border rounded px-2 py-1">
            <span>
              {d.label} <span className="text-gray-400">({d.field_key})</span>
            </span>
            <button type="button" className="text-primary-700" onClick={() => save.mutate(d)}>
              Save
            </button>
          </div>
        ))}
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <ul className="rounded-xl border bg-white divide-y">
        {data.map((f) => (
          <li key={f.field_key} className="px-4 py-2 flex justify-between text-sm">
            <span>
              <span className="font-semibold">{f.label}</span>
              <span className="text-gray-500"> · {f.field_key} · {f.data_type}</span>
            </span>
            <button type="button" className="text-xs text-red-600" onClick={() => remove.mutate(f.field_key)}>
              Hide
            </button>
          </li>
        ))}
        {!isLoading && !data.length && <li className="px-4 py-6 text-sm text-gray-500">No extra fields yet.</li>}
      </ul>
    </div>
  );
}
